import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { showToast } from '../components/Toast';
import BiographyModal from '../admin/BiographyModal';
import '../css/dasboard.css';

function Dashboard() {
  const [biography, setBiography] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showBiographyModal, setShowBiographyModal] = useState(false);
  const [selectedBiography, setSelectedBiography] = useState(null);

  useEffect(() => {
    fetchUserRole();
    fetchBiography();
  }, []);

  const fetchUserRole = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    } catch (error) {
      console.error('Error al obtener rol del usuario:', error);
    }
  };

  const fetchBiography = async () => {
    try {
      setLoading(true);
      setError(null);
      
      try {
        const q = query(collection(db, 'biography'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const biographyData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setBiography(biographyData);
        if (biographyData.length > 0) {
          showToast(`${biographyData.length} entries loaded`, 'success', true);
        } else {
          showToast('No entries available', 'info', true);
        }
      } catch (orderByError) {
        console.warn('No se pudo ordenar por createdAt, intentando sin ordenar...', orderByError);
        
        const querySnapshot = await getDocs(collection(db, 'biography'));
        const biographyData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        biographyData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        
        setBiography(biographyData);
        if (biographyData.length > 0) {
          showToast(`${biographyData.length} entries loaded (sorted locally)`, 'info');
        }
      }
    } catch (error) {
      console.error('Error loading biography:', error);
      
      if (error.code === 'permission-denied') {
        setError('You do not have permission to access this collection');
        showToast('Permission denied - check Firestore rules', 'error', true);
      } else if (error.code === 'not-found') {
        setError('The collection does not exist in Firestore');
        showToast('Collection "biography" not found in Firestore', 'warning');
      } else if (error.message?.includes('Database') || error.message?.includes('not found')) {
        setError('Firestore database not configured correctly');
        showToast('Please create a Firestore database in Firebase Console', 'error', true);
      } else {
        setError('Error connecting to the database');
        showToast('Could not load content', 'error', true);
      }
      
      setBiography([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateField) => {
    if (!dateField) return 'Date not available';
    
    try {
      if (dateField.toDate && typeof dateField.toDate === 'function') {
        return dateField.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      if (dateField instanceof Date) {
        return dateField.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      return new Date(dateField).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date not available';
    }
  };

  const handleEditBiography = (item) => {
    setSelectedBiography(item);
    setShowBiographyModal(true);
  };

  const handleAddBiography = () => {
    setSelectedBiography(null);
    setShowBiographyModal(true);
  };

  const handleModalSuccess = () => {
    fetchBiography();
  };

  // Separate first element (BIO) from the others (SHORT BIO)
  const mainBiography = biography.length > 0 ? biography[0] : null;
  const shortBiographies = biography.slice(1);

  return (
    <div className="dashboard-container">
      {/* About Section */}
      <div className="about-section">
        <div className="about-content">
          <div className="about-logo">
            <img src="/logo.png" alt="Starboy Records Logo" className="about-logo-img" />
          </div>
          
          <div className="about-text-content">
            <h1 className="about-title">Who we are and what we want</h1>
            
            <p className="about-text">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
            </p>
          </div>
        </div>
      </div>

      {/* Biography Section */}
      <div className="biography-section">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading content...</p>
          </div>
        ) : error ? (
          <div className="empty-state">
            <h2>⚠️ Error loading content</h2>
            <p>{error}</p>
            <p className="empty-subtitle">
              {error.includes('Firestore') 
                ? 'You need to create a Firestore database in your Firebase project'
                : 'Try reloading the page'}
            </p>
          </div>
        ) : biography.length === 0 ? (
          <div className="empty-state">
            <h2>📋 No content yet</h2>
            <p>The administrator has not published any entries yet.</p>
            <p className="empty-subtitle">Come back soon for updates!</p>
            {(userRole === 'admin' || userRole === 'superadmin') && (
              <button 
                className="btn-add-content" 
                onClick={handleAddBiography}
              >
                ➕ Add Content
              </button>
            )}
          </div>
        ) : (
          <div className="biography-wrapper">
            <div className="biography-layout">
              {/* Lado Izquierdo - Bio Principal Grande */}
              <div className="biography-left">
                {mainBiography && (
                  <div className="biography-main-card">
                    {mainBiography.imageUrl && (
                      <div className="card-image-large">
                        <img src={mainBiography.imageUrl} alt={mainBiography.title || 'Imagen'} />
                      </div>
                    )}
                    <div className="card-content-large">
                      <h2 className="card-title-large">{mainBiography.title || 'No title'}</h2>
                      <p className="card-description-large">
                        {mainBiography.description || 'No description'}
                      </p>
                      {mainBiography.content && (
                        <div className="card-full-content-large">
                          {mainBiography.content}
                        </div>
                      )}
                    </div>

                    {(userRole === 'admin' || userRole === 'superadmin') && (
                      <div className="card-admin-actions-large">
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEditBiography(mainBiography)}
                          title="Edit"
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="btn-delete-card" 
                          onClick={() => handleEditBiography(mainBiography)}
                          title="Delete"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Lado Derecho - Short Bio en Grid */}
              <div className="biography-right">
                {(userRole === 'admin' || userRole === 'superadmin') && (
                  <button 
                    className="btn-add-content-compact" 
                    onClick={handleAddBiography}
                  >
                    ➕ New
                  </button>
                )}

                <div className="biography-grid-compact">
                  {shortBiographies.map((item) => (
                    <div key={item.id} className="biography-card-small">
                      {item.imageUrl && (
                        <div className="card-image-small">
                          <img src={item.imageUrl} alt={item.title || 'Imagen'} />
                        </div>
                      )}
                      <div className="card-content-small">
                        <h3 className="card-title-small">{item.title || 'No title'}</h3>
                        <p className="card-description-small">
                          {item.description || 'No description'}
                        </p>
                      </div>

                      {(userRole === 'admin' || userRole === 'superadmin') && (
                        <div className="card-actions-small">
                          <button 
                            className="btn-small-edit" 
                            onClick={() => handleEditBiography(item)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-small-delete" 
                            onClick={() => handleEditBiography(item)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showBiographyModal && (
        <BiographyModal 
          onClose={() => setShowBiographyModal(false)}
          biography={selectedBiography}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}

export default Dashboard;
